import { useState } from "react";

import type { McpQuotaPatch } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Card } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * Create a tier by writing its first document.
 *
 * `PATCH /admin/quota/{tier}` on a name with no document creates one, and
 * from then on `GET /admin/tiers` accepts it. A typo in that path is a new
 * tier, so the name is typed twice. The route 422s with no field, so the
 * document starts at `tool_default: -1` (unlimited). Caps are set on the
 * tier's own page.
 */
export function McpNewTier({ config, onCreated }: { config: AppConfig; onCreated: () => void }) {
  const write = useWrite<McpQuotaPatch>(config);
  const [name, setName] = useState("");
  const [again, setAgain] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<McpQuotaPatch | null>(null);

  const trimmed = name.trim();
  const typed = trimmed !== "" || again.trim() !== "";
  const matches = trimmed !== "" && trimmed === again.trim();
  const busy = write.state.kind === "submitting";

  function submit(reason: string) {
    if (!matches) return;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(trimmed)}`, {
        method: "PATCH",
        body: JSON.stringify({
          tool_default: -1,
          ...(reason ? { reason } : {}),
        }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setName("");
        setAgain("");
        onCreated();
      });
    setConfirming(false);
  }

  return (
    <Card
      title="New tier"
      note="A typo in the name creates a tier. Type it twice. It starts unlimited; caps are set on its page."
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          Tier name
          <Input aria-label="New tier name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Type the name again
          <Input
            aria-label="Confirm tier name"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </label>
        {typed && !matches && (
          <p className="text-xs text-amber-700 dark:text-amber-400">The two names do not match.</p>
        )}
        <Gate need="quota:write">
          <Button disabled={!matches || busy} onClick={() => setConfirming(true)}>
            Create MCP tier…
          </Button>
        </Gate>
      </div>

      {result && (
        <div className="mt-3">
          <WriteResult title="Tier created" onDismiss={() => setResult(null)}>
            <p className="text-sm">
              <span className="ref">{result.tier ?? trimmed}</span> exists now.{" "}
              {effectFromSeconds(result.effective_within_seconds, "It")}
            </p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onCreated} />

      <ConfirmDialog
        open={confirming}
        title={`Create ${trimmed}`}
        confirmLabel="Create MCP tier"
        requireReason
        reasonHint="why this tier, e.g. DA-9"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="The name is created when this write lands. Assigning a merchant to it is a separate change."
      >
        <p>
          <span className="ref">{trimmed}</span> starts with an unlimited tool default. Nothing
          moves a merchant onto it until you change their tier.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
